import React, { useEffect, useState } from "react";
import { CheckCircle2, Pause, Play, RotateCcw, Square } from "lucide-react";
import type {
  AcaoCronometro,
  ProducaoBordaItem,
} from "../../../services/painelBordeiroService";
import { formatDuracao } from "../utils";

type Props = {
  item: ProducaoBordaItem;
  /** Diferença (ms) entre o relógio do servidor e o do navegador. */
  offsetMs: number;
  disabled: boolean;
  onAcao: (acao: AcaoCronometro) => void;
};

function useAgora(ativo: boolean): number {
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    if (!ativo) return;
    const id = window.setInterval(() => setAgora(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [ativo]);
  return agora;
}

const STATUS_LABEL: Record<ProducaoBordaItem["cronometro"]["status"], string> =
  {
    parado: "Não iniciado",
    rodando: "Produzindo",
    pausado: "Pausado",
    concluido: "Concluído",
  };

const STATUS_COR: Record<ProducaoBordaItem["cronometro"]["status"], string> = {
  parado: "bg-neutral-100 text-neutral-600",
  rodando: "bg-emerald-100 text-emerald-700",
  pausado: "bg-amber-100 text-amber-700",
  concluido: "bg-sky-100 text-sky-700",
};

const CronometroBorda: React.FC<Props> = ({
  item,
  offsetMs,
  disabled,
  onAcao,
}) => {
  const { status, rodandoDesde, tempoAcumuladoMs } = item.cronometro;
  const agora = useAgora(status === "rodando");
  const decorrido =
    tempoAcumuladoMs +
    (status === "rodando" && rodandoDesde
      ? agora + offsetMs - new Date(rodandoDesde).getTime()
      : 0);

  const botao =
    "inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-sm font-semibold shadow-sm disabled:opacity-40";

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span
          className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_COR[status]}`}
        >
          {STATUS_LABEL[status]}
        </span>
        <span
          className={`font-mono text-2xl font-bold tabular-nums ${
            status === "rodando" ? "text-emerald-700" : "text-neutral-800"
          }`}
        >
          {formatDuracao(decorrido)}
        </span>
      </div>
      <div className="flex gap-2">
        {status === "parado" && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onAcao("iniciar")}
            className={`${botao} bg-emerald-600 text-white hover:bg-emerald-700`}
          >
            <Play className="h-4 w-4" /> Iniciar produção
          </button>
        )}
        {status === "rodando" && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onAcao("pausar")}
            className={`${botao} bg-amber-500 text-white hover:bg-amber-600`}
          >
            <Pause className="h-4 w-4" /> Pausar
          </button>
        )}
        {status === "pausado" && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onAcao("retomar")}
            className={`${botao} bg-emerald-600 text-white hover:bg-emerald-700`}
          >
            <Play className="h-4 w-4" /> Retomar
          </button>
        )}
        {(status === "rodando" || status === "pausado") && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onAcao("concluir")}
            className={`${botao} bg-sky-600 text-white hover:bg-sky-700`}
          >
            <Square className="h-4 w-4" /> Finalizar produção
          </button>
        )}
        {status === "concluido" && (
          <>
            <span className={`${botao} bg-sky-50 text-sky-700 shadow-none`}>
              <CheckCircle2 className="h-4 w-4" /> Produção finalizada
            </span>
            <button
              type="button"
              disabled={disabled}
              onClick={() => onAcao("reabrir")}
              title="Reabrir cronômetro"
              className="inline-flex items-center justify-center rounded-lg border border-neutral-300 px-3 py-2.5 text-neutral-600 hover:bg-neutral-50 disabled:opacity-40"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CronometroBorda;
